import { convex } from "@/lib/convexClient";
import { allCraftspacesDb } from "@/lib/localDb";
const localDb = {
  query: allCraftspacesDb.query.bind(allCraftspacesDb),
  get: async <T>(sql: string, params?: unknown[]): Promise<T | undefined> => {
    const rows = await allCraftspacesDb.query<T>(sql, params);
    return rows[0];
  },
};
import {
  createCmsData,
  removeCmsData,
  updateCmsData,
  bulkReplaceCmsRecords,
  getCmsRecordsByCmsData,
  getAllCmsRecordsByProject,
  updateLocalCmsRecord,
  deleteLocalCmsRecord,
  deleteRecordsByCmsDataId,
} from "@/data/adapters/local/cmsData";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { useEditorStore } from "@/store/editorStore";
import type { Tables } from "@/types/database";
import { parseCSV } from "@/utils/csv";
import { api } from "../../convex/_generated/api";

export interface CMSData {
  id: string;
  project_id: string;
  user_id: string;
  name: string;
  description?: string;
  original_filename?: string;
  json_file_path?: string;
  field_structure?: unknown;
  row_count?: number;
  field_count?: number;
  import_errors?: unknown[];
  file_size?: number;
  created_at?: string;
  updated_at?: string;
}

export interface CMSDataRecord {
  id: string;
  name: string;
  description?: string;
  originalFilename: string;
  jsonFilePath: string;
  fieldStructure: unknown;
  rowCount: number;
  fieldCount: number;
  importErrors: unknown[];
  fileSize: number;
  createdAt: string;
  updatedAt: string;
  data?: unknown; // The actual JSON data when loaded
}

export interface CMSImportOptions {
  name?: string;
  description?: string;
}

export interface CMSUpdateOptions {
  mergeField: string;
  updateExisting?: boolean;
  addNew?: boolean;
}

type CMSJsonData = {
  fields: Array<{
    name: string;
    displayName?: string;
    fieldType?: string;
    isRequired?: boolean;
    dateOptions?: {
      dateFormat?: string;
      includeTime?: boolean;
      timeFormat?: string;
      useSharedTimezone?: boolean;
      timezone?: string;
      displayTimezone?: boolean;
      defaultToCurrentDate?: boolean;
    };
  }>;
  rows: Array<Record<string, unknown>>;
  metadata?: Record<string, unknown>;
};

type GPSLocation = {
  lat?: number;
  lng?: number;
  latitude?: number;
  longitude?: number;
};

// Inline record shape used internally (replaces CMSRecord from indexedDBCMSService)
interface InternalCMSRecord {
  id: string;
  cms_data_id: string;
  data: Record<string, unknown>;
}

const PANORAMAS_COLLECTION_NAME = "Panoramas";
const HOTSPOTS_COLLECTION_NAME = "Hotspots";
const LEGACY_PANORAMAS_COLLECTION_NAME = "Panoramas Collection";
const LEGACY_HOTSPOTS_COLLECTION_NAME = "Hotspots Collection";
const PANORAMAS_BASE_FIELD_NAMES = new Set([
  "id",
  "name",
  "media_type",
  "panorama_file",
  "file_url",
  "file_url_webp",
  "thumbnail_url",
  "thumbnail_url_webp",
  "width",
  "height",
  "file_size",
  "order",
  "file_status",
  "processing_status",
  "private_file_url",
  "coordinates",
  "created_at",
]);
const HOTSPOTS_BASE_FIELD_NAMES = new Set([
  "id",
  "name",
  "type",
  "panorama_id",
  "panorama_name",
  "panorama_file",
  "hotspot_style",
  "visible",
  "order",
  "position_x",
  "position_y",
  "position_z",
  "rotation_x",
  "rotation_y",
  "rotation_z",
  "scale_x",
  "scale_y",
  "scale_z",
  "content",
  "cms_action",
  "created_at",
  "updated_at",
]);

export class CMSService {
  private static getPanoramaMediaTypeLabel(panorama: Tables<"panoramas">): string {
    const data =
      typeof panorama.data === "string"
        ? this.parseJsonValue<Record<string, unknown>>(panorama.data, {})
        : (panorama.data as Record<string, unknown> | null | undefined) || {};
    return data.media_type === "zoomable_image" ? "Zoomimage" : "Panorama";
  }

  private static isLocalBackend(): boolean {
    if (!isElectron()) return false;
    const { projectBackend, project } = useEditorStore.getState();
    if (projectBackend === "local") return true;
    if (!project?.craftspace_id) return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      project.craftspace_id,
    );
  }

  private static parseJsonValue<T>(value: unknown, fallback: T): T {
    if (typeof value === "string" && value.trim().length > 0) {
      try {
        return JSON.parse(value) as T;
      } catch {
        return fallback;
      }
    }
    return (value as T) ?? fallback;
  }

  // ─── CMS Row Data Helpers ──────────────────────────────────────────────────

  private static async bulkReplaceRecords(
    cmsId: string,
    projectId: string,
    rows: Record<string, unknown>[],
    craftspaceId?: string,
  ): Promise<void> {
    if (CMSService.isLocalBackend()) {
      await bulkReplaceCmsRecords(cmsId, projectId, rows, craftspaceId ?? "");
      return;
    }

    // Convex: call bulkReplaceRecords mutation
    await convex.mutation(api.cmsData.bulkReplaceRecords, {
      cms_data_id: cmsId,
      project_id: projectId,
      rows,
    });
  }

  private static async getRecordsByCmsData(
    cmsId: string,
    _projectId: string,
    craftspaceId?: string,
  ): Promise<InternalCMSRecord[]> {
    if (CMSService.isLocalBackend()) {
      const rows = await getCmsRecordsByCmsData(cmsId, craftspaceId ?? "");
      return rows.map((r) => ({
        id: r.id,
        cms_data_id: r.cms_data_id,
        data: r.data,
      }));
    }

    const rows = (await convex.query(api.cmsData.listRecordsByCmsData, {
      cms_data_id: cmsId,
    })) as Array<{ id: string; cms_data_id: string; data: Record<string, unknown> }> | null;
    return (rows || []).map((r) => ({
      id: r.id,
      cms_data_id: r.cms_data_id,
      data: r.data,
    }));
  }

  static async updateDatasetRecord(
    _cmsId: string,
    recordId: string,
    data: Record<string, unknown>,
    craftspaceId?: string,
  ): Promise<void> {
    return CMSService.updateSingleRecord(recordId, data, craftspaceId);
  }

  static async deleteDatasetRecord(_cmsId: string, recordId: string, craftspaceId?: string): Promise<void> {
    return CMSService.deleteSingleRecord(recordId, craftspaceId);
  }

  static async getDatasetRecords(
    cmsId: string,
    projectId: string,
    craftspaceId?: string,
  ): Promise<InternalCMSRecord[]> {
    return CMSService.getRecordsByCmsData(cmsId, projectId, craftspaceId);
  }

  private static async updateSingleRecord(
    recordId: string,
    data: Record<string, unknown>,
    craftspaceId?: string,
  ): Promise<void> {
    if (CMSService.isLocalBackend()) {
      await updateLocalCmsRecord(recordId, data, craftspaceId ?? "");
      return;
    }

    await convex.mutation(api.cmsData.updateCmsRecord, {
      id: recordId,
      data,
    });
  }

  private static async deleteSingleRecord(recordId: string, craftspaceId?: string): Promise<void> {
    if (CMSService.isLocalBackend()) {
      await deleteLocalCmsRecord(recordId, craftspaceId ?? "");
      return;
    }

    await convex.mutation(api.cmsData.deleteCmsRecord, { id: recordId });
  }

  private static async deleteRecordsByCmsId(
    cmsId: string,
    _projectId: string,
    craftspaceId?: string,
  ): Promise<void> {
    if (CMSService.isLocalBackend()) {
      await deleteRecordsByCmsDataId(cmsId, craftspaceId ?? "");
      return;
    }

    await convex.mutation(api.cmsData.deleteRecordsByCmsData, { cms_data_id: cmsId });
  }

  private static async exportAllCmsRecords(
    projectId: string,
    craftspaceId?: string,
  ): Promise<Array<{ cms_data_id: string; id: string; data: Record<string, unknown> }>> {
    if (CMSService.isLocalBackend()) {
      const rows = await getAllCmsRecordsByProject(projectId, craftspaceId ?? "");
      return rows.map((r) => ({
        cms_data_id: r.cms_data_id,
        id: r.id,
        data: r.data,
      }));
    }

    const rows = (await convex.query(api.cmsData.listAllRecordsByProject, {
      project_id: projectId,
    })) as Array<{ id: string; cms_data_id: string; data: Record<string, unknown> }> | null;
    return (rows || []).map((r) => ({
      cms_data_id: r.cms_data_id,
      id: r.id,
      data: r.data,
    }));
  }

  // ─── cms_data metadata helpers ─────────────────────────────────────────────

  private static async listProjectCMSRecords(projectId: string): Promise<CMSData[]> {
    if (CMSService.isLocalBackend()) {
      const rows = await localDb.query<{
        id: string;
        project_id: string;
        name: string;
        description?: string | null;
        original_filename?: string | null;
        json_file_path?: string | null;
        field_structure?: string | null;
        row_count?: number | null;
        field_count?: number | null;
        import_errors?: string | null;
        file_size?: number | null;
        created_at?: string | null;
        updated_at?: string | null;
      }>(
        `SELECT id, project_id, name, description, original_filename, json_file_path,
           field_structure, row_count, field_count, import_errors, file_size,
           created_at, updated_at
         FROM cms_data
         WHERE project_id = ?
         ORDER BY created_at DESC`,
        [projectId],
      );

      return rows.map((row) => ({
        id: row.id,
        project_id: row.project_id,
        user_id: "local",
        name: row.name,
        description: row.description ?? undefined,
        original_filename: row.original_filename ?? undefined,
        json_file_path: row.json_file_path ?? undefined,
        field_structure: CMSService.parseJsonValue(row.field_structure, [] as unknown[]),
        row_count: row.row_count ?? undefined,
        field_count: row.field_count ?? undefined,
        import_errors: CMSService.parseJsonValue(row.import_errors, [] as unknown[]),
        file_size: row.file_size ?? undefined,
        created_at: row.created_at ?? undefined,
        updated_at: row.updated_at ?? undefined,
      }));
    }

    const records = (await convex.query(api.cmsData.listByProject, {
      projectId,
    })) as CMSData[] | null;
    return (records || []) as CMSData[];
  }

  private static async listCMSRecordsByName(projectId: string, name: string): Promise<CMSData[]> {
    const records = await CMSService.listProjectCMSRecords(projectId);
    return records
      .filter((record) => record.name === name)
      .sort(
        (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime(),
      );
  }

  private static async listCMSRecordsByNames(
    projectId: string,
    names: string[],
  ): Promise<CMSData[]> {
    const records = await CMSService.listProjectCMSRecords(projectId);
    return records
      .filter((record) => names.includes(record.name))
      .sort(
        (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime(),
      );
  }

  private static async getCMSRecord(cmsId: string): Promise<CMSData | null> {
    if (CMSService.isLocalBackend()) {
      const row = await localDb.get<{
        id: string;
        project_id: string;
        name: string;
        description?: string | null;
        original_filename?: string | null;
        json_file_path?: string | null;
        field_structure?: string | null;
        row_count?: number | null;
        field_count?: number | null;
        import_errors?: string | null;
        file_size?: number | null;
        created_at?: string | null;
        updated_at?: string | null;
      }>(
        `SELECT id, project_id, name, description, original_filename, json_file_path,
           field_structure, row_count, field_count, import_errors, file_size,
           created_at, updated_at
         FROM cms_data
         WHERE id = ?`,
        [cmsId],
      );

      if (!row) return null;

      return {
        id: row.id,
        project_id: row.project_id,
        user_id: "local",
        name: row.name,
        description: row.description ?? undefined,
        original_filename: row.original_filename ?? undefined,
        json_file_path: row.json_file_path ?? undefined,
        field_structure: CMSService.parseJsonValue(row.field_structure, [] as unknown[]),
        row_count: row.row_count ?? undefined,
        field_count: row.field_count ?? undefined,
        import_errors: CMSService.parseJsonValue(row.import_errors, [] as unknown[]),
        file_size: row.file_size ?? undefined,
        created_at: row.created_at ?? undefined,
        updated_at: row.updated_at ?? undefined,
      };
    }

    const record = (await convex.query(api.cmsData.get, { id: cmsId })) as CMSData | null;
    return record || null;
  }

  private static async createCMSRecord(input: {
    project_id: string;
    name: string;
    description?: string;
    original_filename?: string;
    field_structure?: unknown;
    row_count?: number;
    field_count?: number;
    import_errors?: unknown[];
    file_size?: number;
    craftspace_id?: string;
  }): Promise<CMSData> {
    if (CMSService.isLocalBackend()) {
      const id = await createCmsData({
        craftspace_id: input.craftspace_id ?? "",
        project_id: input.project_id,
        name: input.name,
        description: input.description,
        original_filename: input.original_filename,
        json_file_path: "",
        field_structure: input.field_structure,
        row_count: input.row_count,
        field_count: input.field_count,
        import_errors: input.import_errors,
        file_size: input.file_size,
      });
      const record = await CMSService.getCMSRecord(id);
      if (!record) {
        throw new Error("Failed to create local CMS record");
      }
      return record;
    }

    const record = (await convex.mutation(api.cmsData.create, {
      project_id: input.project_id,
      name: input.name,
      description: input.description,
      original_filename: input.original_filename,
      json_file_path: "",
      field_structure: input.field_structure,
      row_count: input.row_count,
      field_count: input.field_count,
      import_errors: input.import_errors,
      file_size: input.file_size,
    })) as CMSData;
    return record as CMSData;
  }

  private static async updateCMSRecord(
    cmsId: string,
    input: {
      name?: string;
      description?: string;
      original_filename?: string;
      field_structure?: unknown;
      row_count?: number;
      field_count?: number;
      import_errors?: unknown[];
      file_size?: number;
      craftspace_id?: string;
    },
  ): Promise<CMSData> {
    if (CMSService.isLocalBackend()) {
      await updateCmsData({ id: cmsId, craftspace_id: input.craftspace_id ?? "", ...input });
      const record = await CMSService.getCMSRecord(cmsId);
      if (!record) {
        throw new Error("Failed to update local CMS record");
      }
      return record;
    }

    const { craftspace_id: _craftspaceId, ...convexInput } = input;
    const record = (await convex.mutation(api.cmsData.update, {
      id: cmsId,
      ...convexInput,
    })) as CMSData;
    return record as CMSData;
  }

  private static async removeCMSRecord(cmsId: string, craftspaceId?: string): Promise<void> {
    if (CMSService.isLocalBackend()) {
      await removeCmsData(cmsId, craftspaceId ?? "");
      return;
    }

    await convex.mutation(api.cmsData.remove, { id: cmsId });
  }

  private static async listProjectPanoramas(
    projectId: string,
  ): Promise<Array<Tables<"panoramas">>> {
    if (CMSService.isLocalBackend()) {
      const rows = await localDb.query<{
        id: string;
        name: string;
        storage_path?: string | null;
        thumbnail_path?: string | null;
        width?: number | null;
        height?: number | null;
        order_index?: number | null;
        file_status?: string | null;
        processing_status?: string | null;
        metadata?: string | null;
        created_at?: string | null;
      }>(
        `SELECT id, name, storage_path, thumbnail_path, width, height, order_index,
           file_status, processing_status, metadata, created_at
         FROM panoramas
         WHERE project_id = ? AND (status = 'active' OR status IS NULL)
         ORDER BY order_index ASC, created_at ASC`,
        [projectId],
      );

      return rows.map((row) => {
        const metadata = CMSService.parseJsonValue<Record<string, unknown>>(
          row.metadata,
          {},
        );
        const gps_location = (metadata as { gps_location?: GPSLocation }).gps_location ?? null;

        return {
          id: row.id,
          name: row.name,
          file_url: row.storage_path ?? undefined,
          file_url_webp: row.storage_path ?? undefined,
          private_file_url: row.storage_path ?? undefined,
          thumbnail_url: row.thumbnail_path ?? undefined,
          thumbnail_url_webp: row.thumbnail_path ?? undefined,
          width: row.width ?? undefined,
          height: row.height ?? undefined,
          file_size: undefined,
          order: row.order_index ?? 0,
          file_status: row.file_status ?? undefined,
          processing_status: row.processing_status ?? undefined,
          gps_location,
          data: metadata,
          created_at: row.created_at ?? undefined,
        } as Tables<"panoramas">;
      });
    }

    const panoramas = (await convex.query(api.panoramas.listByProject, {
      projectId,
    })) as Array<Tables<"panoramas">> | null;
    return panoramas || [];
  }

  private static async listProjectHotspotsWithPanorama(
    projectId: string,
  ): Promise<
    Array<
      Tables<"hotspots"> & {
        panoramas: {
          id: string;
          name: string;
          thumbnail_url?: string | null;
          thumbnail_url_webp?: string | null;
        } | null;
      }
    >
  > {
    if (CMSService.isLocalBackend()) {
      const hotspots = await localDb.query<{
        id: string;
        panorama_id: string;
        project_id: string;
        type?: string | null;
        label?: string | null;
        content?: string | null;
        position_x?: number | null;
        position_y?: number | null;
        position_z?: number | null;
        rotation_x?: number | null;
        rotation_y?: number | null;
        rotation_z?: number | null;
        scale_x?: number | null;
        scale_y?: number | null;
        scale_z?: number | null;
        visible?: number | null;
        order?: number | null;
        created_at?: string | null;
        updated_at?: string | null;
      }>(
        `SELECT id, panorama_id, project_id, type, label, content,
           position_x, position_y, position_z,
           rotation_x, rotation_y, rotation_z,
           scale_x, scale_y, scale_z,
           visible, \`order\`, created_at, updated_at
         FROM hotspots
         WHERE project_id = ?
         ORDER BY \`order\` ASC, created_at ASC`,
        [projectId],
      );

      const panoramas = await localDb.query<{
        id: string;
        name: string;
        thumbnail_path?: string | null;
      }>(
        `SELECT id, name, thumbnail_path FROM panoramas WHERE project_id = ?`,
        [projectId],
      );
      const panoramaDetails = new Map(
        panoramas.map((panorama) => [
          panorama.id,
          {
            name: panorama.name,
            thumbnail_url: panorama.thumbnail_path ?? null,
          },
        ]),
      );

      return hotspots.map((hotspot) => ({
        id: hotspot.id,
        panorama_id: hotspot.panorama_id,
        project_id: hotspot.project_id,
        type: hotspot.type ?? "navigation",
        name: hotspot.label ?? undefined,
        position:
          hotspot.position_x === null &&
          hotspot.position_y === null &&
          hotspot.position_z === null
            ? undefined
            : {
                x: hotspot.position_x ?? 0,
                y: hotspot.position_y ?? 0,
                z: hotspot.position_z ?? 0,
              },
        rotation: {
          x: hotspot.rotation_x ?? 0,
          y: hotspot.rotation_y ?? 0,
          z: hotspot.rotation_z ?? 0,
        },
        scale: {
          x: hotspot.scale_x ?? 1,
          y: hotspot.scale_y ?? 1,
          z: hotspot.scale_z ?? 1,
        },
        visible: hotspot.visible ? hotspot.visible !== 0 : true,
        order: hotspot.order ?? 0,
        content: CMSService.parseJsonValue<Record<string, unknown>>(hotspot.content, {}),
        created_at: hotspot.created_at ?? undefined,
        updated_at: hotspot.updated_at ?? undefined,
        panoramas: hotspot.panorama_id
          ? {
              id: hotspot.panorama_id,
              name: panoramaDetails.get(hotspot.panorama_id)?.name || "Unknown Panorama",
              thumbnail_url: panoramaDetails.get(hotspot.panorama_id)?.thumbnail_url ?? null,
              thumbnail_url_webp: null,
            }
          : null,
      })) as Array<
        Tables<"hotspots"> & {
          panoramas: {
            id: string;
            name: string;
            thumbnail_url?: string | null;
            thumbnail_url_webp?: string | null;
          } | null;
        }
      >;
    }

    const [hotspots, panoramas] = await Promise.all([
      convex.query(api.hotspots.listByProject, { projectId }) as Promise<Array<
        Tables<"hotspots">
      > | null>,
      convex.query(api.panoramas.listByProject, { projectId }) as Promise<Array<
        Tables<"panoramas">
      > | null>,
    ]);

    const panoramaDetails = new Map(
      (panoramas || []).map((panorama) => [
        panorama.id,
        {
          name: panorama.name || "",
          thumbnail_url: panorama.thumbnail_url ?? null,
          thumbnail_url_webp: panorama.thumbnail_url_webp ?? null,
        },
      ]),
    );

    return (hotspots || []).map((hotspot) => ({
      ...hotspot,
      panoramas: hotspot.panorama_id
        ? {
            id: hotspot.panorama_id,
            name: panoramaDetails.get(hotspot.panorama_id)?.name || "Unknown Panorama",
            thumbnail_url: panoramaDetails.get(hotspot.panorama_id)?.thumbnail_url ?? null,
            thumbnail_url_webp:
              panoramaDetails.get(hotspot.panorama_id)?.thumbnail_url_webp ?? null,
          }
        : null,
    }));
  }

  private static getCustomFieldsFromCollection(
    fieldStructure: unknown[],
    baseFieldNames: Set<string>,
  ): Array<{ name: string; displayName?: string; fieldType?: string; isRequired?: boolean; options?: string[] }> {
    if (!fieldStructure || !Array.isArray(fieldStructure)) return [];
    return (fieldStructure as Array<{
      name?: string;
      displayName?: string;
      fieldType?: string;
      isRequired?: boolean;
      options?: string[];
    }>)
      .filter((field) => field?.name && !baseFieldNames.has(field.name))
      .map((field) => ({
        name: field.name as string,
        displayName: field.displayName,
        fieldType: field.fieldType,
        isRequired: field.isRequired,
        options: field.options,
      }));
  }

  private static mapRowsById(rows: InternalCMSRecord[]): Map<string, Record<string, unknown>> {
    const map = new Map<string, Record<string, unknown>>();
    for (const record of rows) {
      const data = record.data ?? {};
      const rowId = String((data as { id?: unknown }).id ?? "").trim();
      if (!rowId) continue;
      map.set(rowId, data);
    }
    return map;
  }

  static async updateDatasetContent(
    cmsId: string,
    craftspaceId: string,
    data: CMSJsonData,
  ): Promise<CMSDataRecord> {
    const record = await CMSService.getCMSRecord(cmsId);
    if (!record) {
      throw new Error("CMS record not found");
    }

    const projectId = record.project_id;
    const fields = data.fields ?? [];
    const rows = data.rows ?? [];

    // Bulk replace rows in the new backend
    await CMSService.bulkReplaceRecords(cmsId, projectId, rows, craftspaceId);

    const updatedRecord = await CMSService.updateCMSRecord(cmsId, {
      craftspace_id: craftspaceId,
      field_structure: fields,
      row_count: rows.length,
      field_count: fields.length,
    });

    const jsonData: CMSJsonData = {
      fields,
      rows,
      metadata: {
        originalFilename: record.original_filename || "",
        fileSize: JSON.stringify(rows).length,
        importDate: record.created_at,
        rowCount: rows.length,
        fieldCount: fields.length,
        errors: record.import_errors || [],
        ...(data.metadata ?? {}),
      },
    };

    return CMSService.formatCMSRecord(updatedRecord, jsonData);
  }

  /**
   * Import JSON file directly and save to backend
   */
  static async importJsonFile(
    file: File,
    projectId: string,
    _userId: string,
    craftspaceId: string,
    options: CMSImportOptions = {},
  ): Promise<CMSDataRecord> {
    // 1. Parse JSON file
    const fileContent = await file.text();
    let jsonData: unknown;

    try {
      jsonData = JSON.parse(fileContent);
    } catch (_error) {
      throw new Error("Invalid JSON file format");
    }

    // 2. Validate and structure JSON data
    let structuredData: CMSJsonData | undefined;

    if (Array.isArray(jsonData)) {
      // Array of objects - treat as rows
      if (jsonData.length === 0) {
        throw new Error("JSON array is empty");
      }

      // Infer fields from first object
      const firstObject = jsonData[0];
      if (typeof firstObject !== "object" || firstObject === null) {
        throw new Error("JSON array must contain objects");
      }

      const fieldNames = Object.keys(firstObject);
      const fields = fieldNames.map((name) => ({
        name,
        displayName: name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, " $1"),
        fieldType: CMSService.inferJsonFieldType(firstObject[name]),
        isRequired: false,
      }));

      structuredData = {
        fields,
        rows: jsonData,
        metadata: {
          originalFilename: file.name,
          fileSize: file.size,
          importDate: new Date().toISOString(),
          rowCount: jsonData.length,
          fieldCount: fields.length,
          errors: [],
        },
      };
    } else if (typeof jsonData === "object" && jsonData !== null) {
      // Check if it's already in our structured format
      if (
        jsonData.fields &&
        jsonData.rows &&
        Array.isArray(jsonData.fields) &&
        Array.isArray(jsonData.rows)
      ) {
        structuredData = {
          ...jsonData,
          metadata: {
            originalFilename: file.name,
            fileSize: file.size,
            importDate: new Date().toISOString(),
            rowCount: jsonData.rows.length,
            fieldCount: jsonData.fields.length,
            errors: jsonData.metadata?.errors || [],
            ...jsonData.metadata,
          },
        };
      } else {
        // Single object - convert to single row
        const fieldNames = Object.keys(jsonData);
        const fields = fieldNames.map((name) => ({
          name,
          displayName: name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, " $1"),
          fieldType: CMSService.inferJsonFieldType(jsonData[name]),
          isRequired: false,
        }));

        structuredData = {
          fields,
          rows: [jsonData],
          metadata: {
            originalFilename: file.name,
            fileSize: file.size,
            importDate: new Date().toISOString(),
            rowCount: 1,
            fieldCount: fields.length,
            errors: [],
          },
        };
      }
    } else {
      throw new Error("JSON must be an object or array of objects");
    }

    // 3. Create database record first to get the ID
    const safeName = file.name
      .replace(/\.[^/.]+$/, "")
      .replace(/[^a-zA-Z0-9-_]/g, "_")
      .toLowerCase();

    const cmsRecord = {
      craftspace_id: craftspaceId,
      project_id: projectId,
      name: options.name || safeName,
      description: options.description,
      original_filename: file.name,
      field_structure: structuredData.fields,
      row_count: structuredData.rows.length,
      field_count: structuredData.fields.length,
      import_errors: structuredData.metadata.errors,
      file_size: file.size,
    };

    let insertedRecord: CMSData;
    try {
      insertedRecord = await CMSService.createCMSRecord(cmsRecord);
    } catch (error) {
      throw new Error(
        `Failed to save CMS record: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }

    // 4. Use the database ID as the collection ID
    const collectionId = insertedRecord.id;

    // 5. Bulk insert rows into backend
    try {
      await CMSService.bulkReplaceRecords(collectionId, projectId, structuredData.rows, craftspaceId);
    } catch (backendError) {
      // If row insert fails, clean up database record
      try {
        await CMSService.removeCMSRecord(collectionId, craftspaceId);
      } catch (cleanupError) {
        console.warn("Failed to cleanup database record after backend error:", cleanupError);
      }
      throw new Error(
        `Failed to save CMS rows: ${backendError instanceof Error ? backendError.message : "Unknown error"}`,
      );
    }

    // 6. Return formatted record
    return CMSService.formatCMSRecord(insertedRecord, structuredData);
  }

  /**
   * Import CSV file, transform and save to backend
   */
  static async importCsvFile(
    file: File,
    projectId: string,
    _userId: string,
    craftspaceId: string,
    options: CMSImportOptions = {},
  ): Promise<CMSDataRecord> {
    // 1. Parse CSV file
    const csvResult = await parseCSV(file);

    // 2. Create JSON data structure
    const jsonData = {
      fields: csvResult.fields,
      rows: csvResult.rows,
      metadata: {
        originalFilename: file.name,
        fileSize: file.size,
        importDate: new Date().toISOString(),
        rowCount: csvResult.rows.length,
        fieldCount: csvResult.fields.length,
        errors: csvResult.errors,
      },
    };

    // 3. Create database record first to get the ID
    const safeName = file.name
      .replace(/\.[^/.]+$/, "")
      .replace(/[^a-zA-Z0-9-_]/g, "_")
      .toLowerCase();

    const cmsRecord = {
      craftspace_id: craftspaceId,
      project_id: projectId,
      name: options.name || safeName,
      description: options.description,
      original_filename: file.name,
      field_structure: csvResult.fields,
      row_count: csvResult.rows.length,
      field_count: csvResult.fields.length,
      import_errors: csvResult.errors,
      file_size: file.size,
    };

    let insertedRecord: CMSData;
    try {
      insertedRecord = await CMSService.createCMSRecord(cmsRecord);
    } catch (error) {
      throw new Error(
        `Failed to save CMS record: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }

    // 4. Use the database ID as the collection ID
    const collectionId = insertedRecord.id;

    // 5. Bulk insert rows into backend
    try {
      await CMSService.bulkReplaceRecords(collectionId, projectId, csvResult.rows, craftspaceId);
    } catch (backendError) {
      // If row insert fails, clean up database record
      try {
        await CMSService.removeCMSRecord(collectionId, craftspaceId);
      } catch (cleanupError) {
        console.warn("Failed to cleanup database record after backend error:", cleanupError);
      }
      throw new Error(
        `Failed to save CMS rows: ${backendError instanceof Error ? backendError.message : "Unknown error"}`,
      );
    }

    // 6. Return formatted record
    return CMSService.formatCMSRecord(insertedRecord, jsonData);
  }

  /**
   * Create a blank dataset with a default "Name" text field.
   */
  static async createBlankDataset(
    projectId: string,
    _userId: string,
    craftspaceId: string,
    options: CMSImportOptions = {},
  ): Promise<CMSDataRecord> {
    const fields = [
      {
        name: "name",
        displayName: "Name",
        fieldType: "text",
        isRequired: false,
      },
    ];

    const jsonData: CMSJsonData = {
      fields,
      rows: [],
      metadata: {
        originalFilename: "",
        fileSize: 0,
        importDate: new Date().toISOString(),
        rowCount: 0,
        fieldCount: fields.length,
        errors: [],
      },
    };

    const cmsRecord = {
      craftspace_id: craftspaceId,
      project_id: projectId,
      name: options.name || "New table",
      description: options.description,
      original_filename: "",
      field_structure: fields,
      row_count: 0,
      field_count: fields.length,
      import_errors: [],
      file_size: 0,
    };

    let insertedRecord: CMSData;
    try {
      insertedRecord = await CMSService.createCMSRecord(cmsRecord);
    } catch (error) {
      throw new Error(
        `Failed to save CMS record: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }

    return CMSService.formatCMSRecord(insertedRecord, jsonData);
  }

  /**
   * Update existing dataset with new CSV file using field merging
   */
  static async updateDatasetWithCsv(
    datasetId: string,
    file: File,
    projectId: string,
    _userId: string,
    craftspaceId: string,
    options: CMSUpdateOptions,
  ): Promise<CMSDataRecord> {
    // 1. Load existing dataset
    const existingRecord = await CMSService.loadCMSDataWithContent(datasetId, craftspaceId);
    if (!existingRecord) {
      throw new Error("Dataset not found");
    }

    // 2. Parse new CSV file
    const csvResult = await parseCSV(file);

    // 3. Validate merge field exists in both datasets
    const existingFields = existingRecord.data.fields;
    const newFields = csvResult.fields;

    const existingMergeField = existingFields.find((f) => f.name === options.mergeField);
    const newMergeField = newFields.find((f) => f.name === options.mergeField);

    if (!existingMergeField) {
      throw new Error(`Merge field "${options.mergeField}" not found in existing dataset`);
    }

    if (!newMergeField) {
      throw new Error(`Merge field "${options.mergeField}" not found in new CSV file`);
    }

    // 4. Create field mapping for merging
    const fieldMapping = new Map<string, string>();
    newFields.forEach((newField) => {
      const existingField = existingFields.find((f) => f.name === newField.name);
      if (existingField) {
        fieldMapping.set(newField.name, existingField.name);
      }
    });

    // 5. Merge data
    const mergedRows = [...existingRecord.data.rows];
    const newRows = csvResult.rows;
    let updatedCount = 0;
    let addedCount = 0;
    const errors: Array<Record<string, unknown>> = [];

    for (const newRow of newRows) {
      const mergeValue = newRow[options.mergeField];
      if (!mergeValue) {
        errors.push({
          row: newRow,
          error: `Merge field "${options.mergeField}" is empty`,
          type: "merge_field_empty",
        });
        continue;
      }

      // Find existing row with same merge field value
      const existingRowIndex = mergedRows.findIndex(
        (existingRow) => existingRow[options.mergeField] === mergeValue,
      );

      if (existingRowIndex >= 0) {
        // Update existing row
        if (options.updateExisting !== false) {
          const updatedRow = { ...mergedRows[existingRowIndex] };

          // Update fields that exist in both datasets
          fieldMapping.forEach((existingFieldName, newFieldName) => {
            if (newFieldName !== options.mergeField) {
              updatedRow[existingFieldName] = newRow[newFieldName];
            }
          });

          mergedRows[existingRowIndex] = updatedRow;
          updatedCount++;
        }
      } else {
        // Add new row
        if (options.addNew !== false) {
          const newRowData: Record<string, unknown> = {};

          // Map new fields to existing field structure
          fieldMapping.forEach((existingFieldName, newFieldName) => {
            newRowData[existingFieldName] = newRow[newFieldName];
          });

          // Add any new fields that don't exist in existing dataset
          newFields.forEach((newField) => {
            if (!fieldMapping.has(newField.name)) {
              newRowData[newField.name] = newRow[newField.name];
            }
          });

          mergedRows.push(newRowData);
          addedCount++;
        }
      }
    }

    // 6. Update field structure to include any new fields
    const updatedFieldStructure = [...existingFields];
    newFields.forEach((newField) => {
      if (!existingFields.find((f) => f.name === newField.name)) {
        // Find a sample value for this field to infer type
        const sampleValue = newRows.find((row) => row[newField.name] !== undefined)?.[
          newField.name
        ];
        updatedFieldStructure.push({
          name: newField.name,
          displayName: newField.displayName || newField.name,
          fieldType: CMSService.inferJsonFieldType(sampleValue || ""),
        });
      }
    });

    // 7. Create updated JSON data
    const updatedJsonData = {
      fields: updatedFieldStructure,
      rows: mergedRows,
      metadata: {
        ...existingRecord.data.metadata,
        lastUpdate: new Date().toISOString(),
        updateFilename: file.name,
        updateFileSize: file.size,
        updatedRows: updatedCount,
        addedRows: addedCount,
        totalRows: mergedRows.length,
        updateErrors: errors,
      },
    };

    // 8. Bulk replace rows in backend
    await CMSService.bulkReplaceRecords(datasetId, projectId, mergedRows, craftspaceId);

    // 9. Update database record metadata
    const updateData = {
      craftspace_id: craftspaceId,
      field_structure: updatedFieldStructure,
      row_count: mergedRows.length,
      field_count: updatedFieldStructure.length,
      import_errors: errors,
    };

    try {
      await CMSService.updateCMSRecord(datasetId, updateData);
    } catch (error) {
      throw new Error(
        `Failed to update dataset: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }

    // 10. Return updated record
    return CMSService.formatCMSRecord(
      {
        ...existingRecord,
        field_structure: updatedFieldStructure,
        row_count: mergedRows.length,
        field_count: updatedFieldStructure.length,
        import_errors: errors,
        updated_at: new Date().toISOString(),
      },
      updatedJsonData,
    );
  }

  /**
   * Load all CMS data records for a project
   */
  static async loadProjectCMSData(projectId: string): Promise<CMSDataRecord[]> {
    let records: CMSData[];
    try {
      records = await CMSService.listProjectCMSRecords(projectId);
      const existingNames = new Set(records.map((record) => record.name));
      const hasPanoramas =
        existingNames.has(PANORAMAS_COLLECTION_NAME) ||
        existingNames.has(LEGACY_PANORAMAS_COLLECTION_NAME);
      const hasHotspots =
        existingNames.has(HOTSPOTS_COLLECTION_NAME) ||
        existingNames.has(LEGACY_HOTSPOTS_COLLECTION_NAME);

      if (!hasPanoramas || !hasHotspots) {
        const craftspaceId = useEditorStore.getState().project?.craftspace_id || "";
        await CMSService.initializeDefaultCollections(
          projectId,
          CMSService.isLocalBackend(),
          craftspaceId,
        );
        records = await CMSService.listProjectCMSRecords(projectId);
      }
    } catch (error) {
      throw new Error(
        `Failed to load CMS data: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }

    return records.map((record) =>
      CMSService.formatCMSRecord(record, {
        fields: Array.isArray(record.field_structure)
          ? (record.field_structure as CMSJsonData["fields"])
          : [],
        rows: [], // Will be loaded on demand
      }),
    );
  }

  /**
   * Load specific CMS data with content from the backend
   */
  static async loadCMSDataWithContent(cmsId: string, craftspaceId: string): Promise<CMSDataRecord> {
    // 1. Get database record (metadata)
    let record: CMSData | null;
    try {
      record = await CMSService.getCMSRecord(cmsId);
    } catch (error) {
      throw new Error(
        `Failed to load CMS record: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }

    if (!record) {
      throw new Error("CMS record not found");
    }

    const projectId = record.project_id;

    // 2. Check for auto-generated datasets that need refreshing
    const rowCount = record.row_count ?? 0;
    if (CMSService.isLocalBackend() && rowCount > 0) {
      const backendRows = await CMSService.getRecordsByCmsData(cmsId, projectId, craftspaceId);
      if (
        backendRows.length < rowCount &&
        (record.name === HOTSPOTS_COLLECTION_NAME ||
          record.name === LEGACY_HOTSPOTS_COLLECTION_NAME ||
          record.name === PANORAMAS_COLLECTION_NAME ||
          record.name === LEGACY_PANORAMAS_COLLECTION_NAME)
      ) {
        if (
          record.name === HOTSPOTS_COLLECTION_NAME ||
          record.name === LEGACY_HOTSPOTS_COLLECTION_NAME
        ) {
          return await CMSService.generateHotspotsDataset(projectId, "local", craftspaceId);
        }
        return await CMSService.generatePanoramasDataset(projectId, "local", craftspaceId);
      }
    }

    // 3. Load rows from backend
    const backendRows = await CMSService.getRecordsByCmsData(cmsId, projectId, craftspaceId);

    const fieldStructure = Array.isArray(record.field_structure)
      ? (record.field_structure as CMSJsonData["fields"])
      : [];

    if (backendRows.length > 0 || rowCount === 0) {
      const rows = backendRows.map((r) => r.data);

      const jsonData = {
        fields: fieldStructure,
        rows,
        metadata: {
          originalFilename: record.original_filename,
          fileSize: record.file_size || 0,
          importDate: record.created_at,
          rowCount: rows.length,
          fieldCount: fieldStructure.length,
          errors: record.import_errors || [],
        },
      };

      return CMSService.formatCMSRecord(record, jsonData);
    }

    // 4. Fallback: If record has field_structure but no rows, return empty
    if (record.field_structure) {
      const jsonData = {
        fields: fieldStructure,
        rows: [],
        metadata: {
          originalFilename: record.original_filename || "",
          fileSize: record.file_size || 0,
          importDate: record.created_at || new Date().toISOString(),
          rowCount: 0,
          fieldCount: fieldStructure.length,
          errors: record.import_errors || [],
        },
      };
      return CMSService.formatCMSRecord(record, jsonData);
    }

    // 5. No data found
    throw new Error("CMS data not found");
  }

  /**
   * Delete CMS data record from backend and database
   */
  static async deleteCMSData(cmsId: string, craftspaceId: string): Promise<void> {
    // 1. Get record to find project ID
    let record: CMSData | null;
    try {
      record = await CMSService.getCMSRecord(cmsId);
    } catch (error) {
      throw new Error(
        `Failed to find CMS record: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }

    if (!record) {
      throw new Error("CMS record not found");
    }

    const projectId = record.project_id;

    // 2. For Convex, delete rows explicitly (SQLite CASCADE handles it automatically)
    if (!CMSService.isLocalBackend()) {
      try {
        await CMSService.deleteRecordsByCmsId(cmsId, projectId, craftspaceId);
      } catch (err) {
        console.warn("Failed to delete cms_records before deleting cms_data:", err);
      }
    }

    // 3. Delete metadata record (SQLite CASCADE deletes rows automatically)
    try {
      await CMSService.removeCMSRecord(cmsId, craftspaceId);
    } catch (error) {
      throw new Error(
        `Failed to delete CMS record: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * Rename a CMS dataset (collection) by ID.
   */
  static async renameDataset(cmsId: string, name: string, craftspaceId?: string): Promise<CMSData> {
    const trimmed = name.trim();
    if (!trimmed) {
      throw new Error("Dataset name cannot be empty");
    }
    return CMSService.updateCMSRecord(cmsId, { craftspace_id: craftspaceId, name: trimmed });
  }

  /**
   * Export CMS data as CSV
   */
  static async exportAsCSV(cmsData: CMSDataRecord): Promise<void> {
    if (!cmsData.data) {
      throw new Error("CMS data content not loaded");
    }

    const { exportToCSV } = await import("@/utils/csv");
    const filename = `${cmsData.name}_export.csv`;

    exportToCSV(cmsData.data.rows, filename);
  }

  /**
   * Export all CMS data from backend to JSON format for static export.
   * Replaces the old exportIndexedDBToJSON method.
   */
  static async exportCMSDataToJSON(projectId: string): Promise<unknown[]> {
    // Get all cms_data metadata for the project
    const metadataList = await CMSService.listProjectCMSRecords(projectId);

    // Get all rows for the project
    const allRows = await CMSService.exportAllCmsRecords(projectId);

    // Group rows by cms_data_id
    const rowsByCmsDataId = new Map<
      string,
      Array<{ cms_data_id: string; id: string; data: Record<string, unknown> }>
    >();
    for (const row of allRows) {
      const list = rowsByCmsDataId.get(row.cms_data_id) ?? [];
      list.push(row);
      rowsByCmsDataId.set(row.cms_data_id, list);
    }

    // Transform to the format expected by the viewer (same as old exportIndexedDBToJSON)
    const collectionsData: unknown[] = [];

    for (const meta of metadataList) {
      const fieldStructure = Array.isArray(meta.field_structure)
        ? (meta.field_structure as unknown[])
        : [];
      const rows = (rowsByCmsDataId.get(meta.id) ?? []).map((r) => r.data);

      collectionsData.push({
        id: meta.id,
        name: meta.name,
        description: meta.description,
        fieldStructure,
        rows,
        metadata: {
          rowCount: rows.length,
          fieldCount: fieldStructure.length,
          createdAt: meta.created_at,
          updatedAt: meta.updated_at,
        },
      });
    }

    return collectionsData;
  }

  /**
   * Generate panoramas collection automatically from project panoramas
   */
  static async generatePanoramasDataset(
    projectId: string,
    _userId: string,
    craftspaceId: string,
  ): Promise<CMSDataRecord> {
    // 1. Fetch panoramas from database
    let panoramas: Array<Tables<"panoramas">>;
    try {
      panoramas = await CMSService.listProjectPanoramas(projectId);
    } catch (error) {
      throw new Error(
        `Failed to fetch panoramas: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }

    // 2. Create field structure for panoramas dataset
    const fields = [
      { name: "id", displayName: "ID", fieldType: "text", isRequired: true },
      { name: "name", displayName: "Name", fieldType: "text", isRequired: true },
      { name: "media_type", displayName: "Media Type", fieldType: "text", isRequired: true },
      {
        name: "panorama_file",
        displayName: "Linked Panorama",
        fieldType: "panorama_file",
        isRequired: false,
      },
      { name: "file_url", displayName: "File URL", fieldType: "url", isRequired: false },
      { name: "file_url_webp", displayName: "WebP File URL", fieldType: "url", isRequired: false },
      { name: "thumbnail_url", displayName: "Thumbnail URL", fieldType: "url", isRequired: false },
      {
        name: "thumbnail_url_webp",
        displayName: "WebP Thumbnail URL",
        fieldType: "url",
        isRequired: false,
      },
      { name: "width", displayName: "Width", fieldType: "number", isRequired: false },
      { name: "height", displayName: "Height", fieldType: "number", isRequired: false },
      {
        name: "file_size",
        displayName: "File Size (bytes)",
        fieldType: "number",
        isRequired: false,
      },
      { name: "order", displayName: "Order", fieldType: "number", isRequired: true },
      { name: "file_status", displayName: "File Status", fieldType: "text", isRequired: false },
      {
        name: "processing_status",
        displayName: "Processing Status",
        fieldType: "text",
        isRequired: false,
      },
      {
        name: "private_file_url",
        displayName: "Private File URL",
        fieldType: "url",
        isRequired: false,
      },
      { name: "coordinates", displayName: "Coordinates", fieldType: "text", isRequired: false },
      { name: "created_at", displayName: "Created At", fieldType: "date", isRequired: false },
    ];

    // Helper function to format GPS coordinates
    const formatCoordinates = (gpsLocation: GPSLocation | null | undefined): string => {
      if (!gpsLocation) return "";

      // Handle both formats: {lat, lng} from Maps and {latitude, longitude} from EXIF
      const lat = gpsLocation.lat ?? gpsLocation.latitude;
      const lng = gpsLocation.lng ?? gpsLocation.longitude;

      if (lat != null && lng != null) {
        return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      }

      return "";
    };

    // 3. Transform panoramas data to rows
    const baseRows = panoramas.map((panorama) => ({
      id: panorama.id,
      name: panorama.name,
      media_type: CMSService.getPanoramaMediaTypeLabel(panorama),
      panorama_file: panorama.thumbnail_url_webp || panorama.thumbnail_url || "",
      file_url: panorama.file_url,
      file_url_webp: panorama.file_url_webp,
      thumbnail_url: panorama.thumbnail_url,
      thumbnail_url_webp: panorama.thumbnail_url_webp,
      width: panorama.width,
      height: panorama.height,
      file_size: panorama.file_size,
      order: panorama.order,
      file_status: panorama.file_status,
      processing_status: panorama.processing_status,
      private_file_url: panorama.private_file_url,
      coordinates: formatCoordinates(panorama.gps_location),
      created_at: panorama.created_at,
    }));

    // 4. Check if collection already exists
    const existingRecords = await CMSService.listCMSRecordsByNames(projectId, [
      PANORAMAS_COLLECTION_NAME,
      LEGACY_PANORAMAS_COLLECTION_NAME,
    ]);

    let collectionId: string;
    let insertedRecord: CMSData;

    const PANORAMAS_SEEDED_FIELDS: Array<{
      name: string;
      displayName: string;
      fieldType: string;
      isRequired: boolean;
      options?: string[];
    }> = [
      { name: "location", displayName: "Location", fieldType: "singleselect", isRequired: false, options: [] },
      { name: "time", displayName: "Time", fieldType: "text", isRequired: false },
    ];

    let customFields: Array<{
      name: string;
      displayName?: string;
      fieldType?: string;
      isRequired?: boolean;
      options?: string[];
    }> = [];
    let customRowsById = new Map<string, Record<string, unknown>>();

    if (existingRecords && existingRecords.length > 0) {
      const existingRecord = existingRecords[0];
      collectionId = existingRecord.id;

      try {
        const existingRows = await CMSService.getRecordsByCmsData(collectionId, projectId, craftspaceId);
        const existingFieldStructure = Array.isArray(existingRecord.field_structure)
          ? (existingRecord.field_structure as unknown[])
          : [];
        customFields = CMSService.getCustomFieldsFromCollection(
          existingFieldStructure,
          PANORAMAS_BASE_FIELD_NAMES,
        );
        customRowsById = CMSService.mapRowsById(existingRows);
      } catch (error) {
        console.warn("Failed to load custom panorama fields from backend:", error);
      }
    }

    // Inject seeded fields if not already present (preserves existing options/values)
    const seededToAdd = PANORAMAS_SEEDED_FIELDS.filter(
      (s) => !customFields.some((f) => f.name === s.name),
    );
    customFields = [...seededToAdd, ...customFields];

    const fieldsWithCustom = [...fields, ...customFields];
    const rows = baseRows.map((row) => {
      const merged = { ...row } as Record<string, unknown>;
      const customRow = customRowsById.get(String(row.id));
      for (const field of customFields) {
        merged[field.name] = customRow?.[field.name] ?? null;
      }
      return merged;
    });

    if (existingRecords && existingRecords.length > 0) {
      const existingRecord = existingRecords[0];
      collectionId = existingRecord.id;
      insertedRecord = await CMSService.updateCMSRecord(collectionId, {
        craftspace_id: craftspaceId,
        name: PANORAMAS_COLLECTION_NAME,
        field_structure: fieldsWithCustom,
        row_count: rows.length,
        field_count: fieldsWithCustom.length,
      });
    } else {
      // Create new database record first to get the ID
      const cmsRecord = {
        craftspace_id: craftspaceId,
        project_id: projectId,
        name: PANORAMAS_COLLECTION_NAME,
        description: "Automatically generated collection containing all panoramas in this project",
        original_filename: "panoramas_auto_generated.json",
        field_structure: fieldsWithCustom,
        row_count: rows.length,
        field_count: fieldsWithCustom.length,
        import_errors: [],
        file_size: 0,
      };
      const newRecord = await CMSService.createCMSRecord(cmsRecord);
      collectionId = newRecord.id;
      insertedRecord = newRecord;
    }

    // 5. Bulk replace rows in backend
    try {
      await CMSService.bulkReplaceRecords(collectionId, projectId, rows, craftspaceId);
    } catch (backendError) {
      console.error("Failed to save panoramas rows to backend:", backendError);
      // Don't throw - we still have the database record
    }

    // 6. Create JSON data structure for return value
    const jsonData = {
      fields: fieldsWithCustom,
      rows,
      metadata: {
        originalFilename: "panoramas_auto_generated.json",
        fileSize: JSON.stringify(rows).length,
        importDate: new Date().toISOString(),
        rowCount: rows.length,
        fieldCount: fieldsWithCustom.length,
        errors: [],
        isAutoGenerated: true,
        sourceType: "panoramas",
      },
    };

    return CMSService.formatCMSRecord(insertedRecord, jsonData);
  }

  /**
   * Check if panoramas collection exists and refresh it
   */
  static async refreshPanoramasDataset(
    projectId: string,
    userId: string,
    craftspaceId: string,
  ): Promise<CMSDataRecord | null> {
    // 1. Check if panoramas collection already exists
    const existingRecords = await CMSService.listCMSRecordsByNames(projectId, [
      PANORAMAS_COLLECTION_NAME,
      LEGACY_PANORAMAS_COLLECTION_NAME,
    ]);

    // 2. If we have an existing record, update it instead of creating new
    if (existingRecords && existingRecords.length > 0) {
      const existingRecord = existingRecords[0];
      console.log(`Updating existing panoramas collection: ${existingRecord.id}`);

      try {
        // Generate new data
        const panoramas = await CMSService.listProjectPanoramas(projectId);

        // Helper function to format GPS coordinates
        const formatCoordinates = (gpsLocation: GPSLocation | null | undefined): string => {
          if (!gpsLocation) return "";

          const lat = gpsLocation.lat ?? gpsLocation.latitude;
          const lng = gpsLocation.lng ?? gpsLocation.longitude;

          if (lat != null && lng != null) {
            return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
          }

          return "";
        };

        // Define fields for the panoramas dataset
        const fields = [
          { name: "id", displayName: "ID", fieldType: "text", isRequired: true },
          { name: "name", displayName: "Name", fieldType: "text", isRequired: true },
          { name: "media_type", displayName: "Media Type", fieldType: "text", isRequired: true },
          {
            name: "panorama_file",
            displayName: "Linked Panorama",
            fieldType: "panorama_file",
            isRequired: false,
          },
          { name: "file_url", displayName: "File URL", fieldType: "url", isRequired: false },
          {
            name: "file_url_webp",
            displayName: "WebP File URL",
            fieldType: "url",
            isRequired: false,
          },
          {
            name: "thumbnail_url",
            displayName: "Thumbnail URL",
            fieldType: "url",
            isRequired: false,
          },
          {
            name: "thumbnail_url_webp",
            displayName: "WebP Thumbnail URL",
            fieldType: "url",
            isRequired: false,
          },
          { name: "width", displayName: "Width", fieldType: "number", isRequired: false },
          { name: "height", displayName: "Height", fieldType: "number", isRequired: false },
          {
            name: "file_size",
            displayName: "File Size (bytes)",
            fieldType: "number",
            isRequired: false,
          },
          { name: "order", displayName: "Order", fieldType: "number", isRequired: true },
          { name: "file_status", displayName: "File Status", fieldType: "text", isRequired: false },
          {
            name: "processing_status",
            displayName: "Processing Status",
            fieldType: "text",
            isRequired: false,
          },
          {
            name: "private_file_url",
            displayName: "Private File URL",
            fieldType: "url",
            isRequired: false,
          },
          { name: "coordinates", displayName: "Coordinates", fieldType: "text", isRequired: false },
          { name: "created_at", displayName: "Created At", fieldType: "date", isRequired: false },
        ];

        // Transform panoramas data into rows
        const baseRows = panoramas.map((panorama) => ({
          id: panorama.id,
          name: panorama.name,
          media_type: CMSService.getPanoramaMediaTypeLabel(panorama),
          panorama_file: panorama.thumbnail_url_webp || panorama.thumbnail_url || "",
          file_url: panorama.file_url,
          file_url_webp: panorama.file_url_webp,
          thumbnail_url: panorama.thumbnail_url,
          thumbnail_url_webp: panorama.thumbnail_url_webp,
          width: panorama.width,
          height: panorama.height,
          file_size: panorama.file_size,
          order: panorama.order,
          file_status: panorama.file_status,
          processing_status: panorama.processing_status,
          private_file_url: panorama.private_file_url,
          coordinates: formatCoordinates(panorama.gps_location),
          created_at: panorama.created_at,
        }));

        let customFields: Array<{
          name: string;
          displayName?: string;
          fieldType?: string;
          isRequired?: boolean;
        }> = [];
        let customRowsById = new Map<string, Record<string, unknown>>();
        try {
          const existingRows = await CMSService.getRecordsByCmsData(
            existingRecord.id,
            projectId,
            craftspaceId,
          );
          const existingFieldStructure = Array.isArray(existingRecord.field_structure)
            ? (existingRecord.field_structure as unknown[])
            : [];
          customFields = CMSService.getCustomFieldsFromCollection(
            existingFieldStructure,
            PANORAMAS_BASE_FIELD_NAMES,
          );
          customRowsById = CMSService.mapRowsById(existingRows);
        } catch (error) {
          console.warn("Failed to load custom panorama fields from backend:", error);
        }

        const fieldsWithCustom = [...fields, ...customFields];
        const rows = baseRows.map((row) => {
          const merged = { ...row } as Record<string, unknown>;
          const customRow = customRowsById.get(String(row.id));
          for (const field of customFields) {
            merged[field.name] = customRow?.[field.name] ?? null;
          }
          return merged;
        });

        // Create JSON data structure
        const jsonData = {
          fields: fieldsWithCustom,
          rows,
          metadata: {
            generated_at: new Date().toISOString(),
            project_id: projectId,
            total_panoramas: panoramas.length,
            description: "Automatically generated panoramas collection",
          },
        };

        // Bulk replace rows in backend
        await CMSService.bulkReplaceRecords(existingRecord.id, projectId, rows, craftspaceId);

        // Update the existing record metadata
        const updateData = {
          craftspace_id: craftspaceId,
          name: PANORAMAS_COLLECTION_NAME,
          description: "Automatically updated when panoramas change",
          field_structure: fieldsWithCustom,
          row_count: rows.length,
          field_count: fieldsWithCustom.length,
        };
        const updatedRecord = await CMSService.updateCMSRecord(existingRecord.id, updateData);

        return CMSService.formatCMSRecord(updatedRecord, jsonData);
      } catch (error) {
        console.error("Failed to update existing panoramas collection, creating new one:", error);
        // Fall back to creating new collection
      }
    }

    // 3. If no existing record or update failed, create new collection
    console.log("Creating new panoramas collection");
    const result = await CMSService.generatePanoramasDataset(projectId, userId, craftspaceId);

    // 4. Clean up any duplicates after creation
    await CMSService.cleanupDuplicateCollections(projectId, craftspaceId);

    return result;
  }

  /**
   * Generate automated hotspots dataset from project hotspots
   */
  static async generateHotspotsDataset(
    projectId: string,
    _userId: string,
    craftspaceId: string,
  ): Promise<CMSDataRecord> {
    // 1. Fetch hotspots and their associated panorama info from database
    let hotspots: Array<Tables<"hotspots"> & { panoramas: { name: string } | null }>;
    try {
      hotspots = await CMSService.listProjectHotspotsWithPanorama(projectId);
    } catch (error) {
      throw new Error(
        `Failed to fetch hotspots: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }

    // 2. Create field structure for hotspots dataset
    const fields = [
      { name: "id", displayName: "ID", fieldType: "text", isRequired: true },
      { name: "name", displayName: "Name", fieldType: "text", isRequired: true },
      { name: "type", displayName: "Type", fieldType: "text", isRequired: true },
      { name: "panorama_id", displayName: "Panorama ID", fieldType: "text", isRequired: true },
      { name: "panorama_name", displayName: "Panorama Name", fieldType: "text", isRequired: true },
      {
        name: "panorama_file",
        displayName: "Panorama File",
        fieldType: "panorama_file",
        isRequired: false,
      },
      {
        name: "hotspot_style",
        displayName: "Hotspot Style",
        fieldType: "hotspot_style",
        isRequired: false,
      },
      { name: "visible", displayName: "Visible", fieldType: "boolean", isRequired: false },
      { name: "order", displayName: "Order", fieldType: "number", isRequired: true },
      { name: "position_x", displayName: "Position X", fieldType: "number", isRequired: false },
      { name: "position_y", displayName: "Position Y", fieldType: "number", isRequired: false },
      { name: "position_z", displayName: "Position Z", fieldType: "number", isRequired: false },
      { name: "rotation_x", displayName: "Rotation X", fieldType: "number", isRequired: false },
      { name: "rotation_y", displayName: "Rotation Y", fieldType: "number", isRequired: false },
      { name: "rotation_z", displayName: "Rotation Z", fieldType: "number", isRequired: false },
      { name: "scale_x", displayName: "Scale X", fieldType: "number", isRequired: false },
      { name: "scale_y", displayName: "Scale Y", fieldType: "number", isRequired: false },
      { name: "scale_z", displayName: "Scale Z", fieldType: "number", isRequired: false },
      { name: "content", displayName: "Content", fieldType: "text", isRequired: false },
      { name: "cms_action", displayName: "CMS Action", fieldType: "text", isRequired: false },
      { name: "created_at", displayName: "Created At", fieldType: "date", isRequired: false },
      { name: "updated_at", displayName: "Updated At", fieldType: "date", isRequired: false },
    ];

    // 3. Transform hotspots data to rows
    const baseRows = hotspots.map((hotspot) => {
      const contentValue =
        typeof hotspot.content === "string"
          ? CMSService.parseJsonValue<Record<string, unknown>>(hotspot.content, {})
          : (hotspot.content as Record<string, unknown> | null) || {};
      const hotspotStyle = Array.isArray(contentValue.stylePresetIds)
        ? contentValue.stylePresetIds.filter((value): value is string => typeof value === "string").join(",")
        : typeof contentValue.stylePresetId === "string"
          ? contentValue.stylePresetId
          : "";
      const position = hotspot.position || { x: 0, y: 0, z: -5 };
      const rotation = hotspot.rotation || { x: 0, y: 0, z: 0 };
      const scale = hotspot.scale || { x: 1, y: 1, z: 1 };

      return {
        id: hotspot.id,
        name: hotspot.name,
        type: hotspot.type,
        panorama_id: hotspot.panorama_id,
        panorama_name: hotspot.panoramas?.name || "Unknown Panorama",
        panorama_file:
          hotspot.panoramas?.thumbnail_url_webp || hotspot.panoramas?.thumbnail_url || "",
        hotspot_style: hotspotStyle,
        visible: hotspot.visible,
        order: hotspot.order,
        position_x: position.x,
        position_y: position.y,
        position_z: position.z,
        rotation_x: rotation.x,
        rotation_y: rotation.y,
        rotation_z: rotation.z,
        scale_x: scale.x,
        scale_y: scale.y,
        scale_z: scale.z,
        content: JSON.stringify(hotspot.content || {}),
        cms_action: JSON.stringify(hotspot.cms_action || null),
        created_at: hotspot.created_at,
        updated_at: hotspot.updated_at,
      };
    });

    // 4. Check if collection already exists
    const existingRecords = await CMSService.listCMSRecordsByNames(projectId, [
      HOTSPOTS_COLLECTION_NAME,
      LEGACY_HOTSPOTS_COLLECTION_NAME,
    ]);

    let collectionId: string;
    let insertedRecord: CMSData;

    let customFields: Array<{
      name: string;
      displayName?: string;
      fieldType?: string;
      isRequired?: boolean;
    }> = [];
    let customRowsById = new Map<string, Record<string, unknown>>();

    if (existingRecords && existingRecords.length > 0) {
      const existingRecord = existingRecords[0];
      collectionId = existingRecord.id;

      try {
        const existingRows = await CMSService.getRecordsByCmsData(collectionId, projectId, craftspaceId);
        const existingFieldStructure = Array.isArray(existingRecord.field_structure)
          ? (existingRecord.field_structure as unknown[])
          : [];
        customFields = CMSService.getCustomFieldsFromCollection(
          existingFieldStructure,
          HOTSPOTS_BASE_FIELD_NAMES,
        );
        customRowsById = CMSService.mapRowsById(existingRows);
      } catch (error) {
        console.warn("Failed to load custom hotspot fields from backend:", error);
      }
    }

    const fieldsWithCustom = [...fields, ...customFields];
    const rows = baseRows.map((row) => {
      const merged = { ...row } as Record<string, unknown>;
      const customRow = customRowsById.get(String(row.id));
      for (const field of customFields) {
        merged[field.name] = customRow?.[field.name] ?? null;
      }
      return merged;
    });

    if (existingRecords && existingRecords.length > 0) {
      const existingRecord = existingRecords[0];
      collectionId = existingRecord.id;
      insertedRecord = await CMSService.updateCMSRecord(collectionId, {
        craftspace_id: craftspaceId,
        name: HOTSPOTS_COLLECTION_NAME,
        field_structure: fieldsWithCustom,
        row_count: rows.length,
        field_count: fieldsWithCustom.length,
      });
    } else {
      // Create new database record first to get the ID
      const cmsRecord = {
        craftspace_id: craftspaceId,
        project_id: projectId,
        name: HOTSPOTS_COLLECTION_NAME,
        description:
          "Automated collection of all hotspots in this project with panorama connection data",
        original_filename: "hotspots_auto_generated.json",
        field_structure: fieldsWithCustom,
        row_count: rows.length,
        field_count: fieldsWithCustom.length,
        import_errors: [],
        file_size: 0,
      };
      const newRecord = await CMSService.createCMSRecord(cmsRecord);
      collectionId = newRecord.id;
      insertedRecord = newRecord;
    }

    // 5. Bulk replace rows in backend
    try {
      await CMSService.bulkReplaceRecords(collectionId, projectId, rows, craftspaceId);
    } catch (backendError) {
      console.error("Failed to save hotspots rows to backend:", backendError);
      // Don't throw - we still have the database record
    }

    // 6. Create JSON data structure for return value
    const jsonData = {
      fields: fieldsWithCustom,
      rows,
      metadata: {
        originalFilename: "hotspots_auto_generated.json",
        fileSize: JSON.stringify(rows).length,
        importDate: new Date().toISOString(),
        rowCount: rows.length,
        fieldCount: fieldsWithCustom.length,
        errors: [],
        isAutoGenerated: true,
        sourceType: "hotspots",
      },
    };

    return CMSService.formatCMSRecord(insertedRecord, jsonData);
  }

  /**
   * Clean up duplicate auto-generated collections
   */
  static async cleanupDuplicateCollections(projectId: string, craftspaceId: string): Promise<void> {
    console.log("Cleaning up duplicate auto-generated collections...");

    try {
      // Clean up hotspots collections - keep only the most recent one
      const hotspotsRecords = await CMSService.listCMSRecordsByNames(projectId, [
        HOTSPOTS_COLLECTION_NAME,
        LEGACY_HOTSPOTS_COLLECTION_NAME,
      ]);

      if (hotspotsRecords && hotspotsRecords.length > 1) {
        console.log(
          `Found ${hotspotsRecords.length} hotspots collections, keeping the most recent one`,
        );
        // Keep the first (most recent) one, delete the rest
        for (let i = 1; i < hotspotsRecords.length; i++) {
          try {
            await CMSService.deleteCMSData(hotspotsRecords[i].id, craftspaceId);
          } catch (deleteError) {
            console.error("Failed to delete duplicate hotspots collection:", deleteError);
          }
        }
      }
      if (hotspotsRecords && hotspotsRecords.length > 0) {
        const primary = hotspotsRecords[0];
        if (primary.name !== HOTSPOTS_COLLECTION_NAME) {
          await CMSService.updateCMSRecord(primary.id, { craftspace_id: craftspaceId, name: HOTSPOTS_COLLECTION_NAME });
        }
      }

      // Clean up panoramas collections - keep only the most recent one
      const panoramasRecords = await CMSService.listCMSRecordsByNames(projectId, [
        PANORAMAS_COLLECTION_NAME,
        LEGACY_PANORAMAS_COLLECTION_NAME,
      ]);

      if (panoramasRecords && panoramasRecords.length > 1) {
        console.log(
          `Found ${panoramasRecords.length} panoramas collections, keeping the most recent one`,
        );
        // Keep the first (most recent) one, delete the rest
        for (let i = 1; i < panoramasRecords.length; i++) {
          try {
            await CMSService.deleteCMSData(panoramasRecords[i].id, craftspaceId);
          } catch (deleteError) {
            console.error("Failed to delete duplicate panoramas collection:", deleteError);
          }
        }
      }
      if (panoramasRecords && panoramasRecords.length > 0) {
        const primary = panoramasRecords[0];
        if (primary.name !== PANORAMAS_COLLECTION_NAME) {
          await CMSService.updateCMSRecord(primary.id, { craftspace_id: craftspaceId, name: PANORAMAS_COLLECTION_NAME });
        }
      }

      console.log("Cleanup completed");
    } catch (error) {
      console.error("Error during cleanup:", error);
    }
  }

  /**
   * Check if hotspots collection exists and refresh it
   */
  static async refreshHotspotsDataset(
    projectId: string,
    userId: string,
    craftspaceId: string,
  ): Promise<CMSDataRecord | null> {
    // 1. First clean up any existing duplicates
    await CMSService.cleanupDuplicateCollections(projectId, craftspaceId);

    // 2. Check if hotspots collection already exists
    const existingRecords = await CMSService.listCMSRecordsByNames(projectId, [
      HOTSPOTS_COLLECTION_NAME,
      LEGACY_HOTSPOTS_COLLECTION_NAME,
    ]);

    // 3. If we have an existing record, update it instead of creating new
    if (existingRecords && existingRecords.length > 0) {
      const existingRecord = existingRecords[0];
      console.log(`Updating existing hotspots collection: ${existingRecord.id}`);

      try {
        // Generate new data
        const hotspots = (await CMSService.listProjectHotspotsWithPanorama(projectId)).sort(
          (a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime(),
        );

        // Define fields for the hotspots dataset
        const fields = [
          { name: "id", displayName: "ID", fieldType: "text", isRequired: true },
          { name: "name", displayName: "Name", fieldType: "text", isRequired: true },
          { name: "type", displayName: "Type", fieldType: "text", isRequired: true },
          { name: "panorama_id", displayName: "Panorama ID", fieldType: "text", isRequired: true },
          {
            name: "panorama_name",
            displayName: "Panorama Name",
            fieldType: "text",
            isRequired: true,
          },
          {
            name: "panorama_file",
            displayName: "Panorama File",
            fieldType: "panorama_file",
            isRequired: false,
          },
          {
            name: "hotspot_style",
            displayName: "Hotspot Style",
            fieldType: "hotspot_style",
            isRequired: false,
          },
          { name: "content", displayName: "Content", fieldType: "text", isRequired: false },
          { name: "position_x", displayName: "Position X", fieldType: "number", isRequired: false },
          { name: "position_y", displayName: "Position Y", fieldType: "number", isRequired: false },
          { name: "position_z", displayName: "Position Z", fieldType: "number", isRequired: false },
          { name: "rotation_x", displayName: "Rotation X", fieldType: "number", isRequired: false },
          { name: "rotation_y", displayName: "Rotation Y", fieldType: "number", isRequired: false },
          { name: "rotation_z", displayName: "Rotation Z", fieldType: "number", isRequired: false },
          { name: "scale_x", displayName: "Scale X", fieldType: "number", isRequired: false },
          { name: "scale_y", displayName: "Scale Y", fieldType: "number", isRequired: false },
          { name: "scale_z", displayName: "Scale Z", fieldType: "number", isRequired: false },
          { name: "visible", displayName: "Visible", fieldType: "boolean", isRequired: false },
          { name: "order", displayName: "Order", fieldType: "number", isRequired: false },
          {
            name: "created_at",
            displayName: "Created At",
            fieldType: "datetime",
            isRequired: false,
          },
          {
            name: "updated_at",
            displayName: "Updated At",
            fieldType: "datetime",
            isRequired: false,
          },
          { name: "cms_action", displayName: "CMS Action", fieldType: "text", isRequired: false },
        ];

        // Transform hotspots data into rows
        const baseRows = hotspots.map((hotspot) => {
          const contentValue =
            typeof hotspot.content === "string"
              ? CMSService.parseJsonValue<Record<string, unknown>>(hotspot.content, {})
              : (hotspot.content as Record<string, unknown> | null) || {};
          const hasPositionFlag = contentValue.positioned === true;
          const isZeroPosition =
            hotspot.position?.x === 0 &&
            hotspot.position?.y === 0 &&
            hotspot.position?.z === 0;
          const shouldBlankPosition = !hotspot.position || (isZeroPosition && !hasPositionFlag);

          return {
            id: hotspot.id,
            name: hotspot.name || "",
            type: hotspot.type || "",
            panorama_id: hotspot.panorama_id || "",
            panorama_name: hotspot.panoramas?.name || "",
            panorama_file:
              hotspot.panoramas?.thumbnail_url_webp || hotspot.panoramas?.thumbnail_url || "",
            hotspot_style: Array.isArray(contentValue.stylePresetIds)
              ? contentValue.stylePresetIds.filter((value): value is string => typeof value === "string").join(",")
              : typeof contentValue.stylePresetId === "string"
                ? contentValue.stylePresetId
                : "",
            content:
              typeof hotspot.content === "string"
                ? hotspot.content
                : JSON.stringify(hotspot.content || {}),
            position_x: shouldBlankPosition ? null : (hotspot.position?.x ?? null),
            position_y: shouldBlankPosition ? null : (hotspot.position?.y ?? null),
            position_z: shouldBlankPosition ? null : (hotspot.position?.z ?? null),
            rotation_x: hotspot.rotation?.x || 0,
            rotation_y: hotspot.rotation?.y || 0,
            rotation_z: hotspot.rotation?.z || 0,
            scale_x: hotspot.scale?.x || 1,
            scale_y: hotspot.scale?.y || 1,
            scale_z: hotspot.scale?.z || 1,
            visible: hotspot.visible ?? true,
            order: hotspot.order || 0,
            created_at: hotspot.created_at || "",
            updated_at: hotspot.updated_at || "",
            cms_action:
              typeof hotspot.cms_action === "string"
                ? hotspot.cms_action
                : JSON.stringify(hotspot.cms_action || {}),
          };
        });

        let customFields: Array<{
          name: string;
          displayName?: string;
          fieldType?: string;
          isRequired?: boolean;
        }> = [];
        let customRowsById = new Map<string, Record<string, unknown>>();
        try {
          const existingRows = await CMSService.getRecordsByCmsData(
            existingRecord.id,
            projectId,
            craftspaceId,
          );
          const existingFieldStructure = Array.isArray(existingRecord.field_structure)
            ? (existingRecord.field_structure as unknown[])
            : [];
          customFields = CMSService.getCustomFieldsFromCollection(
            existingFieldStructure,
            HOTSPOTS_BASE_FIELD_NAMES,
          );
          customRowsById = CMSService.mapRowsById(existingRows);
        } catch (error) {
          console.warn("Failed to load custom hotspot fields from backend:", error);
        }

        const fieldsWithCustom = [...fields, ...customFields];
        const rows = baseRows.map((row) => {
          const merged = { ...row } as Record<string, unknown>;
          const customRow = customRowsById.get(String(row.id));
          for (const field of customFields) {
            merged[field.name] = customRow?.[field.name] ?? null;
          }
          return merged;
        });

        // Create JSON data structure
        const jsonData = {
          fields: fieldsWithCustom,
          rows,
          metadata: {
            generated_at: new Date().toISOString(),
            project_id: projectId,
            total_hotspots: hotspots.length,
            description: "Automatically generated hotspots collection",
          },
        };

        // Bulk replace rows in backend
        await CMSService.bulkReplaceRecords(existingRecord.id, projectId, rows, craftspaceId);

        // Update the existing record metadata
        const updateData = {
          craftspace_id: craftspaceId,
          name: HOTSPOTS_COLLECTION_NAME,
          description: "Automatically updated when hotspots change",
          field_structure: fieldsWithCustom,
          row_count: rows.length,
          field_count: fieldsWithCustom.length,
        };
        const updatedRecord = await CMSService.updateCMSRecord(existingRecord.id, updateData);

        return CMSService.formatCMSRecord(updatedRecord, jsonData);
      } catch (error) {
        console.error("Failed to update existing hotspots collection, creating new one:", error);
        // Fall back to creating new collection
      }
    }

    // 4. If no existing record or update failed, create new collection
    console.log("Creating new hotspots collection");
    return await CMSService.generateHotspotsDataset(projectId, userId, craftspaceId);
  }

  /**
   * Infer field type from JSON value
   */
  private static inferJsonFieldType(value: unknown): string {
    if (value === null || value === undefined) {
      return "text";
    }

    if (typeof value === "number") {
      return "number";
    }

    if (typeof value === "boolean") {
      return "switch";
    }

    if (typeof value === "string") {
      // Check if it's a URL
      const urlPattern = /^https?:\/\/.+/i;
      if (urlPattern.test(value)) {
        return "url";
      }

      // Check if it's a date
      if (!Number.isNaN(Date.parse(value))) {
        return "date";
      }

      // Check if it's a long text
      if (value.length > 100) {
        return "longtext";
      }

      return "text";
    }

    // For objects, arrays, etc.
    return "text";
  }

  /**
   * Create both Panoramas and Hotspots CMS collections for a newly created project.
   * Accepts an explicit `isLocal` flag so it can be called before the editor store
   * is populated (e.g. right after project creation on the Dashboard).
   */
  static async initializeDefaultCollections(projectId: string, isLocal: boolean, craftspaceId = ""): Promise<void> {
    let existingNames = new Set<string>();
    try {
      existingNames = new Set((await CMSService.listProjectCMSRecords(projectId)).map((r) => r.name));
    } catch (err) {
      console.warn("Failed to inspect existing CMS collections:", err);
    }

    const panoramaFields = [
      { name: "id", displayName: "ID", fieldType: "text", isRequired: true },
      { name: "name", displayName: "Name", fieldType: "text", isRequired: true },
      { name: "panorama_file", displayName: "Linked Panorama", fieldType: "panorama_file", isRequired: false },
      { name: "file_url", displayName: "File URL", fieldType: "url", isRequired: false },
      { name: "file_url_webp", displayName: "WebP File URL", fieldType: "url", isRequired: false },
      { name: "thumbnail_url", displayName: "Thumbnail URL", fieldType: "url", isRequired: false },
      { name: "thumbnail_url_webp", displayName: "WebP Thumbnail URL", fieldType: "url", isRequired: false },
      { name: "width", displayName: "Width", fieldType: "number", isRequired: false },
      { name: "height", displayName: "Height", fieldType: "number", isRequired: false },
      { name: "file_size", displayName: "File Size (bytes)", fieldType: "number", isRequired: false },
      { name: "order", displayName: "Order", fieldType: "number", isRequired: true },
      { name: "file_status", displayName: "File Status", fieldType: "text", isRequired: false },
      { name: "processing_status", displayName: "Processing Status", fieldType: "text", isRequired: false },
      { name: "private_file_url", displayName: "Private File URL", fieldType: "url", isRequired: false },
      { name: "coordinates", displayName: "Coordinates", fieldType: "text", isRequired: false },
      { name: "created_at", displayName: "Created At", fieldType: "date", isRequired: false },
    ];

    const hotspotFields = [
      { name: "id", displayName: "ID", fieldType: "text", isRequired: true },
      { name: "name", displayName: "Name", fieldType: "text", isRequired: true },
      { name: "type", displayName: "Type", fieldType: "text", isRequired: true },
      { name: "panorama_id", displayName: "Panorama ID", fieldType: "text", isRequired: true },
      { name: "panorama_name", displayName: "Panorama Name", fieldType: "text", isRequired: true },
      { name: "panorama_file", displayName: "Panorama File", fieldType: "panorama_file", isRequired: false },
      { name: "hotspot_style", displayName: "Hotspot Style", fieldType: "hotspot_style", isRequired: false },
      { name: "visible", displayName: "Visible", fieldType: "boolean", isRequired: false },
      { name: "order", displayName: "Order", fieldType: "number", isRequired: true },
      { name: "position_x", displayName: "Position X", fieldType: "number", isRequired: false },
      { name: "position_y", displayName: "Position Y", fieldType: "number", isRequired: false },
      { name: "position_z", displayName: "Position Z", fieldType: "number", isRequired: false },
      { name: "rotation_x", displayName: "Rotation X", fieldType: "number", isRequired: false },
      { name: "rotation_y", displayName: "Rotation Y", fieldType: "number", isRequired: false },
      { name: "rotation_z", displayName: "Rotation Z", fieldType: "number", isRequired: false },
      { name: "scale_x", displayName: "Scale X", fieldType: "number", isRequired: false },
      { name: "scale_y", displayName: "Scale Y", fieldType: "number", isRequired: false },
      { name: "scale_z", displayName: "Scale Z", fieldType: "number", isRequired: false },
      { name: "content", displayName: "Content", fieldType: "text", isRequired: false },
      { name: "cms_action", displayName: "CMS Action", fieldType: "text", isRequired: false },
      { name: "created_at", displayName: "Created At", fieldType: "date", isRequired: false },
      { name: "updated_at", displayName: "Updated At", fieldType: "date", isRequired: false },
    ];

    const collections = [
      {
        name: PANORAMAS_COLLECTION_NAME,
        legacyNames: [LEGACY_PANORAMAS_COLLECTION_NAME],
        description: "Automatically generated collection containing all panoramas in this project",
        original_filename: "panoramas_auto_generated.json",
        fields: panoramaFields,
      },
      {
        name: HOTSPOTS_COLLECTION_NAME,
        legacyNames: [LEGACY_HOTSPOTS_COLLECTION_NAME],
        description: "Automatically generated collection containing all hotspots in this project",
        original_filename: "hotspots_auto_generated.json",
        fields: hotspotFields,
      },
    ];

    for (const col of collections) {
      try {
        const hasExisting =
          existingNames.has(col.name) ||
          (col.legacyNames || []).some((legacyName) => existingNames.has(legacyName));
        if (hasExisting) continue;

        if (isLocal) {
          await createCmsData({
            craftspace_id: craftspaceId,
            project_id: projectId,
            name: col.name,
            description: col.description,
            original_filename: col.original_filename,
            json_file_path: "",
            field_structure: col.fields,
            row_count: 0,
            field_count: col.fields.length,
            import_errors: [],
            file_size: 0,
          });
        } else {
          await convex.mutation(api.cmsData.create, {
            project_id: projectId,
            name: col.name,
            description: col.description,
            original_filename: col.original_filename,
            json_file_path: "",
            field_structure: col.fields,
            row_count: 0,
            field_count: col.fields.length,
            import_errors: [],
            file_size: 0,
          });
        }
      } catch (err) {
        console.warn(`Failed to initialize default CMS collection "${col.name}":`, err);
      }
    }
  }

  /**
   * Format database record to CMSDataRecord
   */
  private static formatCMSRecord(record: CMSData, jsonData?: CMSJsonData): CMSDataRecord {
    return {
      id: record.id,
      name: record.name,
      description: record.description || undefined,
      originalFilename: record.original_filename || "",
      jsonFilePath: record.json_file_path || "",
      fieldStructure: record.field_structure || [],
      rowCount: record.row_count || 0,
      fieldCount: record.field_count || 0,
      importErrors: record.import_errors || [],
      fileSize: record.file_size || 0,
      createdAt: record.created_at || new Date().toISOString(),
      updatedAt: record.updated_at || new Date().toISOString(),
      data: jsonData,
    };
  }
}
